'use client';

/**
 * Bulk Actions Toolbar - Appears when entities are selected
 * Provides bulk operations: delete, update status, add/remove tags
 */

import { useState } from 'react';
import type { Entity, EntityStatus } from '@/lib/command-center/types';

interface BulkActionsToolbarProps {
  selectedCount: number;
  selectedEntities: Entity[];
  onClearSelection: () => void;
  onBulkDelete: () => Promise<void>;
  onBulkStatusChange: (status: EntityStatus) => Promise<void>;
  onBulkAddTags: (tags: string[]) => Promise<void>;
  onBulkRemoveTags: (tags: string[]) => Promise<void>;
  availableTags: string[];
}

export default function BulkActionsToolbar({
  selectedCount,
  selectedEntities,
  onClearSelection,
  onBulkDelete,
  onBulkStatusChange,
  onBulkAddTags,
  onBulkRemoveTags,
  availableTags,
}: BulkActionsToolbarProps) {
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [showTagsMenu, setShowTagsMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [tagAction, setTagAction] = useState<'add' | 'remove'>('add');
  const [newTag, setNewTag] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleStatusChange = async (status: EntityStatus) => {
    setIsLoading(true);
    try {
      await onBulkStatusChange(status);
      setShowStatusMenu(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    setIsLoading(true);
    try {
      await onBulkDelete();
      setShowDeleteConfirm(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddTag = async () => {
    if (!newTag.trim()) return;
    setIsLoading(true);
    try {
      await onBulkAddTags([newTag.trim()]);
      setNewTag('');
      setShowTagsMenu(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveTag = async (tag: string) => {
    setIsLoading(true);
    try {
      await onBulkRemoveTags([tag]);
    } finally {
      setIsLoading(false);
    }
  };

  // Get common tags among all selected entities
  const commonTags = selectedEntities.reduce((acc, entity, index) => {
    const entityTags = entity.tags || [];
    if (index === 0) return entityTags;
    return acc.filter(tag => entityTags.includes(tag));
  }, [] as string[]);

  return (
    <>
      <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
        <div className="bg-surface border border-border rounded-xl shadow-2xl px-4 py-3 flex items-center gap-4">
          {/* Selection count */}
          <div className="flex items-center gap-2">
            <span className="bg-accent text-accent-foreground font-bold px-2.5 py-1 rounded-full text-sm">
              {selectedCount}
            </span>
            <span className="text-foreground-secondary text-sm">selected</span>
          </div>

          <div className="h-6 w-px bg-cream/20" />

          {/* Actions */}
          <div className="flex items-center gap-2">
            {/* Status dropdown */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowStatusMenu(!showStatusMenu);
                  setShowTagsMenu(false);
                }}
                disabled={isLoading}
                className="px-3 py-1.5 rounded-lg text-sm text-foreground-secondary hover:bg-cream/10 transition-colors flex items-center gap-1.5 disabled:opacity-50"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Status
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showStatusMenu && (
                <div className="absolute bottom-full left-0 mb-2 bg-background border border-border rounded-lg shadow-xl py-1 min-w-32">
                  {(['active', 'inactive', 'deprecated'] as EntityStatus[]).map((status) => (
                    <button
                      key={status}
                      onClick={() => handleStatusChange(status)}
                      className="w-full px-3 py-1.5 text-left text-sm text-foreground-secondary hover:bg-cream/10 transition-colors capitalize"
                    >
                      {status}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Tags dropdown */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowTagsMenu(!showTagsMenu);
                  setShowStatusMenu(false);
                }}
                disabled={isLoading}
                className="px-3 py-1.5 rounded-lg text-sm text-foreground-secondary hover:bg-cream/10 transition-colors flex items-center gap-1.5 disabled:opacity-50"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
                Tags
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showTagsMenu && (
                <div className="absolute bottom-full left-0 mb-2 bg-background border border-border rounded-lg shadow-xl p-3 min-w-64">
                  {/* Tab buttons */}
                  <div className="flex gap-2 mb-3">
                    <button
                      onClick={() => setTagAction('add')}
                      className={`flex-1 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                        tagAction === 'add'
                          ? 'bg-accent text-accent-foreground'
                          : 'text-foreground-tertiary hover:text-foreground'
                      }`}
                    >
                      Add Tag
                    </button>
                    <button
                      onClick={() => setTagAction('remove')}
                      className={`flex-1 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                        tagAction === 'remove'
                          ? 'bg-accent text-accent-foreground'
                          : 'text-foreground-tertiary hover:text-foreground'
                      }`}
                    >
                      Remove Tag
                    </button>
                  </div>

                  {tagAction === 'add' && (
                    <div>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newTag}
                          onChange={(e) => setNewTag(e.target.value)}
                          placeholder="New tag name..."
                          className="flex-1 bg-surface border border-border rounded px-2 py-1.5 text-sm text-foreground placeholder:text-foreground-muted focus:border-accent outline-none"
                          onKeyDown={(e) => e.key === 'Enter' && handleAddTag()}
                        />
                        <button
                          onClick={handleAddTag}
                          disabled={!newTag.trim() || isLoading}
                          className="px-3 py-1.5 bg-accent text-accent-foreground rounded text-sm font-medium disabled:opacity-50"
                        >
                          Add
                        </button>
                      </div>
                      {availableTags.length > 0 && (
                        <div className="mt-2">
                          <p className="text-foreground-muted text-xs mb-1">Quick add:</p>
                          <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                            {availableTags.slice(0, 10).map(tag => (
                              <button
                                key={tag}
                                onClick={() => onBulkAddTags([tag])}
                                className="px-2 py-0.5 bg-cream/10 text-foreground-secondary text-xs rounded hover:bg-cream/20 transition-colors"
                              >
                                {tag}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {tagAction === 'remove' && (
                    <div>
                      {commonTags.length > 0 ? (
                        <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto">
                          {commonTags.map(tag => (
                            <button
                              key={tag}
                              onClick={() => handleRemoveTag(tag)}
                              className="px-2 py-0.5 bg-red-900/50 text-red-400 text-xs rounded hover:bg-red-900/70 transition-colors flex items-center gap-1"
                            >
                              {tag}
                              <span>&times;</span>
                            </button>
                          ))}
                        </div>
                      ) : (
                        <p className="text-foreground-muted text-sm">No common tags to remove</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Delete button */}
            <button
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isLoading}
              className="px-3 py-1.5 rounded-lg text-sm text-red-400 hover:bg-red-900/30 transition-colors flex items-center gap-1.5 disabled:opacity-50"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete
            </button>
          </div>

          <div className="h-6 w-px bg-cream/20" />

          {/* Clear selection */}
          <button
            onClick={onClearSelection}
            className="text-foreground-tertiary hover:text-foreground text-sm transition-colors"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-background border border-border rounded-xl p-6 max-w-md w-full mx-4">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-900/30 mb-4">
                <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Delete {selectedCount} entities?</h3>
              <p className="text-foreground-tertiary text-sm mb-6">
                This action cannot be undone. All selected entities and their relationships will be permanently deleted.
              </p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 border border-border rounded-lg text-foreground hover:border-border/40 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={isLoading}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {isLoading ? 'Deleting...' : 'Delete All'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
